import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Docket primitives shared by the homepage sections: a 12-column frame split
 * into a 3-column margin (labels, ref codes, footnotes) and a 9-column body.
 * Every row is tied together by one hairline that runs across both columns.
 */

export const DOCKET_CONTAINER = 'mx-auto w-full max-w-[1200px] px-5 md:px-6'

const buttonBase =
  'h-12 rounded-none border-2 px-6 font-body text-xs font-bold uppercase tracking-[0.14em] shadow-none hover:scale-100 hover:shadow-none'

/** Ink-filled button: the default action inside the page body. */
export const inkButton = cn(
  buttonBase,
  'border-foreground bg-foreground text-background hover:bg-foreground/85 hover:text-background'
)

/** Outlined button: the secondary action. Inverts on hover. */
export const lineButton = cn(
  buttonBase,
  'border-foreground bg-transparent text-foreground hover:border-foreground hover:bg-foreground hover:text-background'
)

/** One-line mono header strip that opens every section under a 2px rule. */
export function DocketHeader({ fields, topRule = true }: { fields: string[]; topRule?: boolean }) {
  return (
    <div className={cn('border-b border-foreground/20', topRule && 'border-t-2 border-t-foreground')}>
      <div
        className={cn(
          DOCKET_CONTAINER,
          'flex flex-wrap items-center gap-x-3 gap-y-1 py-2.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground'
        )}
      >
        {fields.map((field, index) => (
          <React.Fragment key={field}>
            {index > 0 && <span aria-hidden>{'//'}</span>}
            <span className={index === 0 ? 'font-bold text-foreground' : undefined}>{field}</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  )
}

/** A margin + body row. The hairline on top spans both columns. */
export function DocketRow({
  margin,
  children,
  rule = true,
  className,
  bodyClassName,
}: {
  margin?: React.ReactNode
  children: React.ReactNode
  rule?: boolean
  className?: string
  bodyClassName?: string
}) {
  return (
    <div className={DOCKET_CONTAINER}>
      <div
        className={cn(
          'grid grid-cols-1 lg:grid-cols-12 lg:gap-x-6',
          rule && 'border-t border-foreground/20',
          className
        )}
      >
        <aside className="pt-5 lg:col-span-3 lg:border-r lg:border-foreground/20 lg:py-8 lg:pr-6">
          {margin}
        </aside>
        <div className={cn('min-w-0 pb-8 pt-4 lg:col-span-9 lg:py-8', bodyClassName)}>{children}</div>
      </div>
    </div>
  )
}

/** `[ DOCKET 03 ]` plus a short reference code, for the margin column. */
export function DocketLabel({ index, code }: { index: string; code: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 lg:block">
      <p className="text-xs font-bold uppercase tracking-[0.14em] text-foreground">[ DOCKET {index} ]</p>
      <p className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground lg:mt-2">{code}</p>
    </div>
  )
}

/** A numbered sidenote. Lives in the margin, level with the claim it explains. */
export function MarginNote({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <p className="mt-3 flex gap-2 text-[11px] leading-relaxed text-muted-foreground first:mt-0 lg:mt-4">
      <sup className="top-0 shrink-0 text-[11px] font-bold text-foreground">{n}</sup>
      <span>{children}</span>
    </p>
  )
}

/** Superscript marker in the body that points at a MarginNote. */
export function Ref({ n }: { n: number }) {
  return (
    <sup className="-top-[0.9em] ml-0.5 font-body text-[0.5em] font-bold tracking-normal text-current">
      {n}
    </sup>
  )
}

/** `label ........ value` with a dotted leader, typewriter style. */
export function LeaderRow({
  label,
  value,
  className,
}: {
  label: React.ReactNode
  value: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex items-baseline gap-2 py-2 text-sm', className)}>
      <dt className="shrink-0">{label}</dt>
      <span aria-hidden className="min-w-4 flex-1 translate-y-[-3px] border-b border-dotted border-current opacity-40" />
      <dd className="min-w-0 text-right tabular-nums">{value}</dd>
    </div>
  )
}
